import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Grid, Chip, Button, CircularProgress,
  LinearProgress, Alert, AlertTitle, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Link, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material'
import { Refresh, OpenInNew, CheckCircle, Warning, Cancel, HelpOutline, ArrowBack, InfoOutlined, LibraryBooks } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { analysisApi, complianceAddonApi } from '../services/api'
import { RootState } from '../store'
import { useEntitlements } from '../hooks/useEntitlements'
import ComplianceIntegrations from '../components/ComplianceIntegrations'
import BundleTeaser from '../components/BundleTeaser'
import { BUNDLE_COPY } from '../components/bundleCopy'
import useCanContactVendor from '../hooks/useCanContactVendor'
import BundleBadge from '../components/BundleBadge'

type Visibility = 'full' | 'teaser' | 'upsell' | 'hidden'

interface FrameworkSummary {
  framework: string
  display_name: string
  controls_assessed: number
  aligned: number
  partial: number
  gap: number
  not_checked: number
  // Provenance of the edition assessed against. A posture figure that cannot
  // name its source is not something an auditor can act on.
  version?: string
  publisher?: string
  source_url?: string
  citation?: string
}

interface ControlPosture {
  control_id: string
  control_name?: string
  status: 'aligned' | 'partial' | 'gap' | 'not_checked'
  findings_count: number
  rules_with_findings: number
  rules_mapped: number
  evidence_categories: string[]
  citation?: string
}

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  aligned: 'success',
  partial: 'warning',
  gap: 'error',
  not_checked: 'default',
}

const STATUS_ICONS: Record<string, JSX.Element> = {
  aligned: <CheckCircle fontSize="small" />,
  partial: <Warning fontSize="small" />,
  gap: <Cancel fontSize="small" />,
  not_checked: <HelpOutline fontSize="small" />,
}

const STATUS_TOOLTIPS: Record<string, string> = {
  aligned: 'No ARIAS rule mapped to this control raised a finding. This reflects the absence of issues, not positive verification that the control objective is met.',
  partial: 'Some active findings on your agents, but no high or critical severity',
  gap: 'At least one high or critical finding on rules ARIAS maps to this control',
  not_checked: 'Nothing ARIAS detects maps to this control, so it was not assessed. This is not a statement that the control does not apply to you.',
}

// Displayed labels, chosen to map 1:1 onto the OSCAL objective-status values
// used by the export — so a customer reading the dashboard and an auditor
// reading the exported assessment-results see the same four states under
// names that mean the same thing.
//
// 'No gaps found' deliberately does NOT say 'Satisfied'. ARIAS derives it
// from the absence of findings and cannot distinguish "the rule evaluated
// this agent and it passed" from "the rule never applied to this codebase".
// Calling that Satisfied on screen would claim verification that did not
// happen, and the export states the same basis in its `reason` field.
const STATUS_LABELS: Record<string, string> = {
  aligned: 'No gaps found',      // OSCAL: satisfied (with basis in `reason`)
  partial: 'Partially satisfied', // OSCAL: other
  gap: 'Not satisfied',           // OSCAL: not-satisfied
  not_checked: 'Not assessed',    // OSCAL: omitted from findings; see remarks
}

const CompliancePosturePage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id
  const navigate = useNavigate()
  const { has } = useEntitlements()
  const hasCustomFramework = has('custom_framework')

  const [loading, setLoading] = useState(true)
  const [visibility, setVisibility] = useState<Visibility>('hidden')
  // WHICH upsell. Returning only 'upsell' put the Business $499/mo add-on
  // in front of an Enterprise tenant the moment Compliance Posture moved
  // into ARIAS Evidence — the wrong price for the wrong tier.
  const [upsellReason, setUpsellReason] = useState<string | null>(null)
  const [bundledIn, setBundledIn] = useState<string | null>(null)
  const canContactVendor = useCanContactVendor()
  const [trialCap, setTrialCap] = useState<number | null>(null)
  const [frameworks, setFrameworks] = useState<FrameworkSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [selectedFramework, setSelectedFramework] = useState<string | null>(null)
  const [controls, setControls] = useState<ControlPosture[]>([])
  const [teaserTruncated, setTeaserTruncated] = useState(false)
  const [postureLoading, setPostureLoading] = useState(false)
  const [drillOpen, setDrillOpen] = useState(false)
  const [drillData, setDrillData] = useState<any | null>(null)
  // Repository scoping
  const [allRepos, setAllRepos] = useState<{ repository_name: string; scanned_at?: string }[]>([])
  const [selectedRepo, setSelectedRepo] = useState<string>('') // '' = all repos rollup
  const [scopedRepos, setScopedRepos] = useState<{ repository_name: string; scanned_at?: string }[]>([])
  // History chart — points: {scanned_at, aligned, partial, gap, not_checked, aligned_pct}
  const [history, setHistory] = useState<any[]>([])

  useEffect(() => {
    if (tenantId) {
      fetchRepos()
      fetchFrameworks(selectedRepo)
    }
  }, [tenantId])

  useEffect(() => {
    if (tenantId) {
      fetchFrameworks(selectedRepo)
      if (selectedFramework) openFramework(selectedFramework)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRepo])

  const fetchRepos = async () => {
    try {
      const resp = await analysisApi.getComplianceRepositories()
      setAllRepos(resp.data?.repositories || [])
    } catch {
      setAllRepos([])
    }
  }

  const fetchFrameworks = async (repo?: string) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await analysisApi.getComplianceFrameworks(repo || undefined)
      setVisibility(resp.data.visibility)
      setUpsellReason(resp.data.upsell_reason ?? null)
      setBundledIn(resp.data.bundled_in ?? null)
      setFrameworks(resp.data.frameworks || [])
      setTrialCap(resp.data.trial_cap ?? null)
      setScopedRepos(resp.data.scoped_repos || [])
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setError('Compliance Posture is not enabled for your account.')
      } else {
        setError('Unable to load frameworks. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const openFramework = async (framework: string) => {
    setSelectedFramework(framework)
    setPostureLoading(true)
    setControls([])
    setHistory([])
    try {
      const [postureResp, histResp] = await Promise.allSettled([
        analysisApi.getCompliancePosture(framework, selectedRepo || undefined),
        analysisApi.getCompliancePostureHistory(framework, selectedRepo || undefined),
      ])
      if (postureResp.status === 'fulfilled') {
        setControls(postureResp.value.data.controls || [])
        setTeaserTruncated(!!postureResp.value.data.teaser_truncated)
      } else {
        setControls([])
      }
      if (histResp.status === 'fulfilled') {
        setHistory(histResp.value.data?.points || [])
      }
      // Auto-scroll to the posture table
      setTimeout(() => {
        const el = document.getElementById('posture-table')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } finally {
      setPostureLoading(false)
    }
  }

  const closeFramework = () => {
    setSelectedFramework(null)
    setControls([])
  }

  const openControlDrilldown = async (framework: string, controlId: string) => {
    setDrillOpen(true)
    setDrillData(null)
    try {
      const resp = await analysisApi.getComplianceControlDetail(framework, controlId, selectedRepo || undefined)
      setDrillData(resp.data)
    } catch {
      setDrillData({ error: 'Failed to load control detail' })
    }
  }

  const refresh = async () => {
    await fetchRepos()
    await fetchFrameworks(selectedRepo)
    if (selectedFramework) {
      await openFramework(selectedFramework)
    }
  }

  const startAddonCheckout = async () => {
    if (!tenantId) return
    try {
      const resp = await complianceAddonApi.startCheckout({
        tenant_id: tenantId,
        billing_email: user?.email || '',
        tenant_name: (user as any)?.tenant_name || '',
      })
      if (resp.data?.checkout_url) {
        window.location.href = resp.data.checkout_url
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Unable to start checkout. Contact sales@tryarias.com.')
    }
  }

  const renderPercentAssessed = (f: FrameworkSummary) => {
    const assessed = f.controls_assessed > 0 ? Math.round(100 * (f.controls_assessed - f.not_checked) / f.controls_assessed) : 0
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">% Controls evidenced</Typography>
          <Typography variant="caption" fontWeight={600}>{assessed}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={assessed}
          sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.200' }}
        />
      </Box>
    )
  }

  const renderSummaryCard = (f: FrameworkSummary) => (
    <Card
      key={f.framework}
      sx={{
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: 4 },
        height: '100%',
        border: selectedFramework === f.framework ? '2px solid' : '1px solid',
        borderColor: selectedFramework === f.framework ? 'primary.main' : 'divider',
      }}
      onClick={() => openFramework(f.framework)}
    >
      <CardContent>
        <Typography variant="h6">{f.display_name}</Typography>
        {f.version && (
          <Tooltip title={f.citation || ''}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {f.version}
              {f.publisher ? ` · ${f.publisher}` : ''}
            </Typography>
          </Tooltip>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, mt: 1 }}>
          <Chip size="small" color="success" label={`${f.aligned} no gaps found`} />
          <Chip size="small" color="warning" label={`${f.partial} partially satisfied`} />
          <Chip size="small" color="error" label={`${f.gap} not satisfied`} />
          <Chip size="small" label={`${f.not_checked} not assessed`} />
        </Box>
        {renderPercentAssessed(f)}
        <Tooltip title="ARIAS provides evidence for these controls from the findings its scans produce. Frameworks have additional controls outside ARIAS's scope — organizational policies, training data governance, contractual arrangements — which are shown as not assessed rather than assumed compliant.">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5, cursor: 'help' }}
          >
            {f.controls_assessed} controls ARIAS assesses
            <InfoOutlined sx={{ fontSize: 12 }} />
          </Typography>
        </Tooltip>
      </CardContent>
    </Card>
  )

  // ---- Render states ----

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || visibility === 'hidden') {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Compliance Posture</Typography>
        <Alert severity="info">
          <AlertTitle>Not available</AlertTitle>
          {error || 'Compliance Posture is included with the Enterprise plan, or available as a $499/mo add-on on the Business plan.'}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="span">Compliance Posture</Typography>
          {/* Server-answered. `visibility === 'full'` covers BOTH an
              Enterprise tenant with Evidence and a Business tenant with the
              $499 add-on, so the client cannot tell them apart — and badging
              the second as ARIAS Evidence would name a bundle they are not
              being sold. */}
          {bundledIn === 'evidence' && (
            <BundleBadge
              bundle="ARIAS Evidence"
              refused={upsellReason === 'evidence_bundle'}
            />
          )}
          <Typography variant="body2" color="text.secondary">
            How your agents align to compliance frameworks. Click a framework to see per-control posture.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Frameworks a customer brought themselves route evidence exactly
              like the published ones, so this is where they look for them. */}
          {hasCustomFramework && (
            <Button size="small" startIcon={<LibraryBooks />} onClick={() => navigate('/custom-rules?tab=frameworks')}>
              My frameworks
            </Button>
          )}
          {allRepos.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Repository</InputLabel>
              <Select
                label="Repository"
                value={selectedRepo}
                onChange={(e) => setSelectedRepo(e.target.value as string)}
              >
                <MenuItem value="">All repos (rollup)</MenuItem>
                {allRepos.map((r) => (
                  <MenuItem key={r.repository_name} value={r.repository_name}>
                    {r.repository_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <IconButton onClick={refresh} aria-label="Refresh"><Refresh /></IconButton>
        </Box>
      </Box>

      {scopedRepos.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {selectedRepo
            ? `Posture as of ${scopedRepos[0]?.scanned_at ? new Date(scopedRepos[0].scanned_at).toLocaleDateString() : 'latest scan'} — ${selectedRepo}`
            : `Posture across ${scopedRepos.length} repo${scopedRepos.length === 1 ? '' : 's'}, each using its most-recent scan (worst-of rollup per control).`}
        </Typography>
      )}

      {/* An eligible tier that has not bought ARIAS Evidence gets the bundle
          teaser, with the blurred posture table behind it. Offering them the
          Business add-on would quote a price that does not apply to them. */}
      {visibility === 'upsell' && upsellReason === 'evidence_bundle' && (
        <Box sx={{ mb: 3 }}>
          <BundleTeaser
            copy={BUNDLE_COPY.compliance_posture}
            canContactVendor={canContactVendor}
          />
        </Box>
      )}

      {visibility === 'upsell' && upsellReason !== 'evidence_bundle' && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button color="primary" variant="contained" size="small" onClick={startAddonCheckout}>
              Add for $499/mo
            </Button>
          }
        >
          <AlertTitle>Compliance Posture add-on</AlertTitle>
          See how your agents align to EU AI Act, NIST AI RMF, ISO 42001, SOC 2, GDPR, OWASP LLM/Agentic Top 10, and MAESTRO. Drill into every gap and track posture over time.
        </Alert>
      )}

      {visibility === 'teaser' && trialCap !== null && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Trial preview</AlertTitle>
          You're seeing framework summaries plus the top {trialCap} controls per framework. Upgrade to Enterprise or add the $499/mo Business add-on to see full drill-down.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {frameworks.map((f) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={f.framework}>
            {renderSummaryCard(f)}
          </Grid>
        ))}
      </Grid>

      {selectedFramework && (
        <Card id="posture-table">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Link
                component="button"
                onClick={closeFramework}
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: 2 }}
              >
                <ArrowBack fontSize="small" sx={{ mr: 0.5 }} />
                All frameworks
              </Link>
              <Typography variant="h6">
                {frameworks.find((f) => f.framework === selectedFramework)?.display_name} — Per-Control Posture
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              <strong>No gaps found</strong>: no mapped ARIAS rule raised a finding — absence of issues, not positive verification.
              <strong style={{ marginLeft: 8 }}>Partially satisfied</strong>: low/medium findings present.
              <strong style={{ marginLeft: 8 }}>Not satisfied</strong>: high/critical findings present.
              <strong style={{ marginLeft: 8 }}>Not assessed</strong>: nothing ARIAS detects maps to this control — not a statement that it doesn't apply to you.
              <br />These map 1:1 onto the OSCAL states used in the export, so the dashboard and the exported evidence agree.
            </Typography>

            {/* Posture history chart — stacked area showing aligned/partial/gap over time */}
            {history.length >= 2 && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Posture trend — last {history.length} scan{history.length === 1 ? '' : 's'}
                </Typography>
                <svg viewBox={`0 0 ${history.length * 40} 80`} width="100%" height="80" preserveAspectRatio="none" style={{ display: 'block' }}>
                  {(() => {
                    const W = history.length * 40
                    const H = 80
                    const maxTotal = Math.max(...history.map((p) => p.aligned + p.partial + p.gap)) || 1
                    const x = (i: number) => (i / Math.max(1, history.length - 1)) * W
                    const yScale = (v: number) => H - (v / maxTotal) * H
                    // Build stacked polygons: aligned on bottom, partial above, gap on top
                    const alignedPts = history.map((p, i) => `${x(i)},${yScale(p.aligned)}`).join(' ')
                    const partialPts = history.map((p, i) => `${x(i)},${yScale(p.aligned + p.partial)}`).join(' ')
                    return (
                      <>
                        {/* aligned (success) */}
                        <polygon points={`0,${H} ${alignedPts} ${W},${H}`} fill="#4caf50" opacity="0.6" />
                        {/* partial (warning) */}
                        <polygon points={`${alignedPts} ${history.map((p, i) => `${x(i)},${yScale(p.aligned + p.partial)}`).reverse().join(' ')}`} fill="#ff9800" opacity="0.6" />
                        {/* gap (error) */}
                        <polygon points={`${partialPts} ${history.map((p, i) => `${x(i)},${yScale(p.aligned + p.partial + p.gap)}`).reverse().join(' ')}`} fill="#f44336" opacity="0.7" />
                        {/* aligned line on top for emphasis */}
                        <polyline points={alignedPts} fill="none" stroke="#2e7d32" strokeWidth="1.5" />
                      </>
                    )
                  })()}
                </svg>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {history[0]?.scanned_at ? new Date(history[0].scanned_at).toLocaleDateString() : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip size="small" sx={{ bgcolor: '#4caf50', color: 'white', height: 18 }} label="aligned" />
                    <Chip size="small" sx={{ bgcolor: '#ff9800', color: 'white', height: 18 }} label="partial" />
                    <Chip size="small" sx={{ bgcolor: '#f44336', color: 'white', height: 18 }} label="gap" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {history[history.length - 1]?.scanned_at ? new Date(history[history.length - 1].scanned_at).toLocaleDateString() : ''}
                  </Typography>
                </Box>
              </Box>
            )}

            {teaserTruncated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Showing first {trialCap} of {frameworks.find((f) => f.framework === selectedFramework)?.controls_assessed} controls. Upgrade to see all.
              </Alert>
            )}
            {postureLoading ? (
              <CircularProgress size={20} />
            ) : controls.length === 0 ? (
              <Typography color="text.secondary">No control data available.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Control</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Rules with findings / total rules ARIAS maps to this control">
                          <span style={{ cursor: 'help' }}>Checks failing</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Total active findings across all mapped rules">
                          <span style={{ cursor: 'help' }}>Findings</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>Evidence from</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {controls.map((c) => (
                      <TableRow key={c.control_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{c.control_id}</Typography>
                          {c.control_name && (
                            <Typography variant="caption" color="text.secondary">{c.control_name}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={STATUS_TOOLTIPS[c.status]}>
                            <Chip
                              size="small"
                              color={STATUS_COLORS[c.status]}
                              icon={STATUS_ICONS[c.status]}
                              label={STATUS_LABELS[c.status] || c.status}
                              sx={{ cursor: 'help' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {c.rules_mapped > 0 ? `${c.rules_with_findings} / ${c.rules_mapped}` : '—'}
                        </TableCell>
                        <TableCell align="right">{c.findings_count}</TableCell>
                        <TableCell>
                          <Tooltip title={(c.evidence_categories || []).join(', ') || 'No findings reached this control on the latest scan'}>
                            <Typography variant="caption" color="text.secondary">
                              {(c.evidence_categories || []).slice(0, 2).join(', ') || '—'}
                              {(c.evidence_categories || []).length > 2 && ` +${c.evidence_categories.length - 2}`}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {c.rules_mapped > 0 ? (
                            <Tooltip title="View rule-by-rule details and findings">
                              <IconButton
                                size="small"
                                onClick={() => openControlDrilldown(selectedFramework, c.control_id)}
                                aria-label="View rules and findings"
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Nothing ARIAS detects maps to this control — there is nothing to drill into">
                              <span><IconButton size="small" disabled><OpenInNew fontSize="small" /></IconButton></span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={drillOpen} onClose={() => setDrillOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {drillData?.framework_display_name && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {drillData.framework_display_name}
            </Typography>
          )}
          {drillData?.control_id} — {drillData?.control_name || 'Control detail'}
        </DialogTitle>
        <DialogContent dividers>
          {!drillData ? (
            <CircularProgress size={20} />
          ) : drillData.error ? (
            <Alert severity="error">{drillData.error}</Alert>
          ) : (
            <>
              {drillData.citation && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} gutterBottom>
                  Source: {drillData.citation}
                </Typography>
              )}
              {drillData.descriptor && (
                <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                  {drillData.descriptor}
                </Typography>
              )}

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                Why these findings are evidence for this control
              </Typography>
              {(drillData.evidence_categories || []).length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Nothing ARIAS detects maps to this control, so it is reported as not assessed.
                </Typography>
              ) : (
                <Box sx={{ mb: 1 }}>
                  {(drillData.evidence_categories || []).map((e: any) => (
                    <Box key={e.category} sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {e.category}
                        {' · '}
                        {e.strength}
                        {' · '}
                        {e.findings_count} finding{e.findings_count === 1 ? '' : 's'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {e.rationale}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {(drillData.scf_references || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                    Secure Controls Framework cross-references
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                    {(drillData.scf_references || []).map((r: any) => (
                      <Tooltip key={r.scf_control_id} title={`${r.scf_control_name || ''}${r.scf_domain ? ` · ${r.scf_domain}` : ''}`}>
                        <Chip size="small" variant="outlined" label={r.scf_control_id} />
                      </Tooltip>
                    ))}
                  </Box>
                  {drillData.scf_attribution && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {drillData.scf_references[0]?.source_version} ·{' '}
                      {drillData.scf_attribution.creator} ·{' '}
                      <Link href={drillData.scf_attribution.license_url} target="_blank" rel="noopener noreferrer">
                        {drillData.scf_attribution.license}
                      </Link>
                      {' — '}{drillData.scf_attribution.disclaimer}
                    </Typography>
                  )}
                </>
              )}

              {(drillData.rules || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                    Your custom rules that fired
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Rules your team authored. ARIAS's own rules are not listed individually — what
                    determines this control's state is the category of finding they produce, shown above.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Rule</TableCell>
                          <TableCell>Highest severity</TableCell>
                          <TableCell align="right">Findings on your agents</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {drillData.rules.map((r: any) => (
                          <TableRow key={r.rule_id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{r.name || r.rule_id}</Typography>
                              {r.name && (
                                <Typography variant="caption" color="text.secondary">{r.rule_id}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {r.severity ? (
                                <Chip
                                  size="small"
                                  label={r.severity}
                                  color={r.severity === 'critical' || r.severity === 'high' ? 'error' : r.severity === 'medium' ? 'warning' : 'default'}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" color="error" label={r.findings_count} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>
                Active findings ({(drillData.findings || []).length})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                These are the specific findings on your agents that produced this control's status.
              </Typography>
              {(drillData.findings || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No active findings reached this control on the latest scan.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Finding</TableCell>
                        <TableCell>Evidence category</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Detected</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drillData.findings.map((f: any) => (
                        <TableRow key={f.finding_id}>
                          <TableCell>{f.title}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">{f.category || '—'}</Typography>
                            {f.rule_id && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                your rule: {f.rule_id}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={f.severity} color={f.severity === 'critical' || f.severity === 'high' ? 'error' : 'warning'} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{f.created_at ? new Date(f.created_at).toLocaleDateString() : '-'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrillOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Integrations sub-section — admin-only; renders only if visibility allows */}
      <ComplianceIntegrations
        tenantTier={(user as any)?.tenant_tier || ''}
        visibility={visibility}
        apiBaseUrl={typeof window !== 'undefined' ? window.location.origin : 'https://app.tryarias.com'}
      />
    </Box>
  )
}

export default CompliancePosturePage
